import { useMemo } from "react";
import useSWR from "swr";
import { debounce } from "@/utils/debounce";
import {
  statisticalAnalysisService,
  StatisticalAnalysisResponse,
  StatisticalAnalysisParams,
} from "@/services/statistical-analysis-service";

interface UseStatisticalAnalysisParams {
  neighborhood: string | null | undefined;
  monthlyPrice: string | undefined;
  capacity: string | undefined;
  sizeM2: string | undefined;
  enabled?: boolean;
}

const cleanCurrencyValue = (value: string): number => {
  if (!value || value.trim() === "") return 0;
  const cleanedValue = value.replace(/\./g, "").replace(",", ".");
  return parseFloat(cleanedValue) || 0;
};

const toNumber = (value: string | undefined): number => {
  if (!value || value.trim() === "") return 0;
  return parseFloat(value) || 0;
};

const createAnalysisKey = (
  neighborhood: string,
  params: StatisticalAnalysisParams,
): string => {
  return `statistical-analysis-${neighborhood}-${params.monthlyPrice}-${params.capacity}-${params.sizeM2}`;
};

const fetcher = async ([neighborhood, params]: [
  string,
  StatisticalAnalysisParams,
]) => {
  try {
    const result = await statisticalAnalysisService.getStatisticalAnalysis(
      neighborhood,
      params,
    );
    return result;
  } catch (error) {
    console.error("❌ Statistical Analysis Fetcher - Error:", error);
    throw error;
  }
};

export function useStatisticalAnalysis({
  neighborhood,
  monthlyPrice,
  capacity,
  sizeM2,
  enabled = true,
}: UseStatisticalAnalysisParams) {
  const params: StatisticalAnalysisParams | null = useMemo(() => {
    if (!capacity || !sizeM2) {
      return null;
    }

    const capacityNum = toNumber(capacity);
    const sizeM2Num = toNumber(sizeM2);

    if (capacityNum <= 0 || sizeM2Num <= 0) {
      return null;
    }

    const monthlyPriceNum = monthlyPrice ? cleanCurrencyValue(monthlyPrice) : 0;

    return {
      monthlyPrice: monthlyPriceNum,
      capacity: capacityNum,
      sizeM2: sizeM2Num,
    };
  }, [monthlyPrice, capacity, sizeM2]);

  const trimmedNeighborhood = neighborhood?.trim() ?? "";

  const swrKey = useMemo(() => {
    if (!enabled || !params || !trimmedNeighborhood) {
      return null;
    }
    return createAnalysisKey(trimmedNeighborhood, params);
  }, [enabled, trimmedNeighborhood, params]);

  const { data, error, isLoading, isValidating, mutate } =
    useSWR<StatisticalAnalysisResponse>(
      swrKey ? [trimmedNeighborhood, params] : null,
      fetcher,
      {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        dedupingInterval: 30000,
        errorRetryCount: 2,
        errorRetryInterval: 1000,
      },
    );

  const debouncedRefetch = useMemo(
    () => debounce(() => mutate(), 500),
    [mutate],
  );

  const hasNeighborhood = Boolean(trimmedNeighborhood);
  const hasRequiredData = Boolean(hasNeighborhood && params);
  const hasSizeCapacityData = Boolean(params);

  const localSizeCapacityRatio = useMemo(() => {
    if (!sizeM2 || !capacity) return null;

    const sizeNum = toNumber(sizeM2);
    const capacityNum = toNumber(capacity);

    if (sizeNum <= 0 || capacityNum <= 0) return null;

    const ratio = sizeNum / capacityNum;
    const isWithinStandard = ratio >= 2.0;
    const status = isWithinStandard ? "within_standard" : "below_minimum";

    return {
      ratio,
      isWithinStandard,
      status,
      warning: !isWithinStandard
        ? `Proporção de ${ratio.toFixed(1)} m²/pessoa está abaixo do recomendado (mínimo 2,0 m²/pessoa).`
        : null,
    };
  }, [sizeM2, capacity]);

  const hasExtremeOutliers = useMemo(() => {
    let isExtremePrice = false;
    let isExtremeRatio = false;

    if (data) {
      const { analyses } = data;
      if (
        [
          analyses.monthlyPrice?.outlierDetection.status,
          analyses.capacity?.outlierDetection.status,
          analyses.sizeM2?.outlierDetection.status,
        ].every((status) => status === "insufficient_data")
      ) {
        return {
          isExtremePrice: false,
          isExtremeRatio: false,
          hasAnyExtreme: false,
        };
      }
    }

    if (data) {
      const { analyses, spaceRatioValidation } = data;
      isExtremePrice =
        analyses.monthlyPrice?.outlierDetection?.status !== "within_range";
      isExtremeRatio = spaceRatioValidation?.status === "below_minimum";
    } else if (localSizeCapacityRatio) {
      isExtremeRatio = localSizeCapacityRatio.status === "below_minimum";
    }

    return {
      isExtremePrice,
      isExtremeRatio,
      hasAnyExtreme: isExtremePrice || isExtremeRatio,
    };
  }, [data, localSizeCapacityRatio]);

  return {
    data,
    error,
    isLoading,
    isValidating,
    hasNeighborhood,
    hasRequiredData,
    hasSizeCapacityData,
    hasExtremeOutliers,
    refetch: debouncedRefetch,
    localSizeCapacityRatio,
    hasPriceOutlier:
      data?.analyses?.monthlyPrice?.outlierDetection?.status !== "within_range",
    hasCapacityOutlier:
      data?.analyses?.capacity?.outlierDetection?.status !== "within_range",
    hasSizeOutlier:
      data?.analyses?.sizeM2?.outlierDetection?.status !== "within_range",
    isRatioWithinStandard:
      data?.spaceRatioValidation?.isWithinStandard ?? false,
  };
}
